import { useState, useEffect } from 'react'
import { Room, getRoomsByCompany } from '@/lib/api/rooms/rooms-api'
import { useAuth } from '@/store/hooks'
import { MdMeetingRoom } from 'react-icons/md'

interface RoomSelectorProps {
    value?: number | null
    onChange: (roomId: number | null) => void
    disabled?: boolean
    placeholder?: string
    showNoneOption?: boolean
}

export function RoomSelector({
    value,
    onChange,
    disabled = false,
    placeholder = 'Selecione uma sala',
    showNoneOption = true,
}: RoomSelectorProps) {
    const { user } = useAuth()
    const [rooms, setRooms] = useState<Room[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadRooms()
        }
    }, [user?.companyId])

    const loadRooms = async () => {
        if (!user?.companyId) return

        setLoading(true)
        try {
            const response = await getRoomsByCompany(user.companyId)
            if (response.success) {
                setRooms(response.result.filter(r => r.active))
            }
        } catch (error) {
            console.error('Erro ao carregar salas:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value
        onChange(val ? parseInt(val) : null)
    }

    const selectedRoom = rooms.find(r => r.id === value)

    return (
        <div className="form-control w-full">
            <div className="relative">
                <select
                    className="select select-bordered w-full pl-10"
                    value={value?.toString() || ''}
                    onChange={handleChange}
                    disabled={disabled || loading}
                >
                    {showNoneOption && <option value="">{placeholder}</option>}
                    {loading && <option value="">Carregando...</option>}
                    {rooms.map(room => (
                        <option key={room.id} value={room.id.toString()}>
                            {room.name}
                            {room.capacity && ` (${room.capacity} pessoas)`}
                        </option>
                    ))}
                </select>
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {selectedRoom?.color ? (
                        <div
                            className="w-5 h-5 rounded"
                            style={{ backgroundColor: selectedRoom.color }}
                        />
                    ) : (
                        <MdMeetingRoom className="text-lg text-base-content/50" />
                    )}
                </div>
            </div>
        </div>
    )
}

interface RoomBadgeProps {
    room: {
        id: number
        name: string
        color?: string
    }
    size?: 'xs' | 'sm' | 'md'
}

export function RoomBadge({ room, size = 'sm' }: RoomBadgeProps) {
    const sizeClasses = {
        xs: 'px-1.5 py-0.5 text-xs gap-1',
        sm: 'px-2 py-1 text-xs gap-1.5',
        md: 'px-3 py-1.5 text-sm gap-2',
    }

    return (
        <span
            className={`inline-flex items-center rounded-full bg-base-200 ${sizeClasses[size]}`}
        >
            {room.color && (
                <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: room.color }}
                />
            )}
            <MdMeetingRoom className="text-base-content/60" />
            <span className="text-base-content">{room.name}</span>
        </span>
    )
}
